import { silentMagazineDiagnostics } from "./diagnostics.js";
import { articlePresentation } from "../../../lib/articles/presentation.js";
import { authorSlug, authorPath } from "../../../lib/articles/normalizeAuthors.js";
import { normalizeTags } from "../../../lib/articles/tags.js";
import { magazineCategorySections, magazineImage } from "./sections.js";
import { resolveNavigationItems } from "../../../lib/publications/navigation.js";
import { magazineArticleMatchesCategory } from "../../../lib/articles/magazineTaxonomy.js";

const clean = value => typeof value === "string" ? value.trim() : "";

export function magazineReadingTime(content) {
  const text = clean(content).replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ").replace(/&(?:#\d+|#x[\da-f]+|[a-z]+);/gi, " ").trim();
  return text ? Math.max(1, Math.ceil(text.split(/\s+/).length / 200)) : null;
}

export function toMagazineArticle(article = {}, settings = {}) {
  const authors = Array.isArray(article.authors) ? article.authors : [];
  const author = authors[0] || (article.author && typeof article.author === "object" ? article.author : { id: article.authorId, name: article.author });
  const category = article.category?.name || article.category || article.categories?.[0]?.name || "News";
  return {
    id: article.id || article.slug || article.title,
    slug: article.slug,
    href: article.url || `/${article.slug}`,
    title: clean(article.title) || "Untitled",
    ...articlePresentation(article),
    image: magazineImage(article.image || article.featuredImage || article.heroImage, settings),
    category,
    categorySlug: article.categorySlug || article.category?.slug || String(category).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
    author: author.name || article.author || "Editorial Team",
    authorSlug: authorSlug(author),
    authorUrl: author.url || article.authorUrl || authorPath(author, { settings }),
    authorImage: author.image || article.authorAvatar || null,
    date: article.publishedAt || article.date || article.createdAt || null,
    content: article.content || article.body || "",
    featured: Boolean(article.featured || article.isFeatured),
    tags: normalizeTags(article.tags),
    raw: article,
  };
}

export function toMagazineArticles(articles = [], settings = {}) {
  return articles.filter(Boolean).map(article => toMagazineArticle(article, settings));
}

const normalizedLabel = value => clean(value).toLowerCase().replace(/[-_]+/g, " ");

export function magazineLink(value) {
  const url = clean(value);
  if (/^\/(?!\/)/.test(url) || /^https?:\/\//i.test(url)) return url;
  return null;
}

function configuredNavigation(settings) {
  return [...(settings?.navigation?.main || []), ...(settings?.navigation?.header || [])];
}

export function buildMagazineNavigation(settings = {}, categories = [], pages = []) {
  if (Array.isArray(settings.navigation?.header)) return resolveNavigationItems(settings.navigation.header, settings, categories, pages);
  return buildMagazineSectionNavigation(settings, categories);
}

export function buildMagazineHomepageNavigation(settings = {}, categories = []) {
  return buildMagazineSectionNavigation({ ...settings, navigation: { sections: settings.navigation?.sections } }, categories);
}

function buildMagazineSectionNavigation(settings = {}, categories = []) {
  categories = categories.filter(category => !category.deletedAt && (!settings.publicationId || category.publicationId === settings.publicationId));
  const configuredSections = settings.navigation?.sections;
  if (Array.isArray(configuredSections)) return configuredSections.flatMap(item => {
    if (item.url === "/games") return [{ label: clean(item.label) || "Games", href: "/games" }];
    const category = categories.find(category => category.id === (item.categoryId || item.target)
      || item.url === `/category/${category.slug}`);
    return category ? [{ label: clean(item.label) || category.name, href: `/category/${category.slug}`, categoryId: category.id }] : [];
  }).filter((item, index, all) => all.findIndex(other => other.href === item.href) === index);
  return ["News", "Politics", "Business", "Entertainment", "Lifestyle", "Sports", "Technology", "Games"].map(label => {
    const aliases = label === "News" ? ["news", "latest news", "local news"] : [normalizedLabel(label)];
    const configured = configuredNavigation(settings).find(item => aliases.includes(normalizedLabel(item.label)));
    const category = categories.find(item => item.id === (configured?.categoryId || (configured?.type === "category" ? configured.target : null)))
      || categories.find(item => aliases.includes(normalizedLabel(item.name || item.title)) || aliases.includes(normalizedLabel(item.slug)));
    const href = label === "Games" ? "/games"
      : (category?.slug ? `/category/${category.slug}` : magazineLink(configured?.url))
        || (label === "News" ? "/#latest-news" : null);
    return { label, href, categoryId: category?.id };
  });
}

export function getMagazineEditions(settings = {}) {
  const menu = configuredNavigation(settings).find(item => ["our editions", "our editions:", "editions"].includes(normalizedLabel(item.label)));
  return (menu?.children || []).map(item => ({ label: clean(item.label), href: magazineLink(item.url) }))
    .filter(item => item.label && item.href);
}

export function getMagazineHeaderName(settings = {}) {
  // The publication/organization name is distinct from its SEO title/tagline.
  return clean(settings.organization?.name) || clean(settings.general?.publicationName) || clean(settings.name) || "Magazine";
}

export function formatMagazineHeaderDate(settings = {}, now = new Date()) {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: settings.publicationTimeZone || "Africa/Johannesburg",
    weekday: "long", day: "numeric", month: "long", year: "numeric"
  }).format(now);
}

export function buildMagazineHomeViewModel(articles = [], settings = {}, resolvedBlocks = [], categories = [], diagnostics = silentMagazineDiagnostics) {
  return diagnostics.observe("buildMagazineHomeViewModel", () => {
    diagnostics.record("buildMagazineHomeViewModel.inputs", { articles, settings, blocks: resolvedBlocks, categories });
    return buildHomeView(articles, settings, resolvedBlocks, categories, diagnostics);
  }, "view");
}
function buildHomeView(articles, settings, resolvedBlocks, categories, diagnostics) {
  const blocks = resolvedBlocks.filter(block => block.enabled !== false && block.type !== "roundup");
  const eligible = article => article?.status === "published" && !article.deletedAt && article.slug
    && (!settings.publicationId || article.publicationId === settings.publicationId);
  const unique = items => items.filter((item, index, all) => all.findIndex(other => other.id === item.id) === index);
  const convert = source => unique(source.filter(eligible).map(article => {
    const category = categories.find(item => item.id === article.categoryId);
    return toMagazineArticle(category ? { ...article, category } : article, settings);
  }));
  const fromBlock = type => convert(blocks.find(block => block.type === type)?.data?.articles || []);
  const items = convert([...articles, ...blocks.flatMap(block => block.data?.articles || [])])
    .sort((left, right) => (Date.parse(right.date) || 0) - (Date.parse(left.date) || 0));
  diagnostics.record("buildMagazineHomeViewModel.items", { items, blocks }, { items: "articles" });
  const configuredHero = fromBlock("hero");
  const hero = configuredHero.filter(item => item.id === settings.homepage?.featuredArticleId).slice(0, 1);

  const navigation = buildMagazineHomepageNavigation(settings, categories);
  const categorySections = magazineCategorySections(settings, categories, navigation).map(section => {
    const block = blocks.find(block => block.id === section.id && block.props?.categoryId === section.category.id);
    const source = block ? convert(block.data?.articles || []) : items;
    const limit = Math.min(50, Math.max(1, parseInt(section.props.limit, 10) || 5));
    return { ...section, href: `/category/${section.category.slug}`, articles: source
      .filter(item => magazineArticleMatchesCategory(item.raw, section.category, categories))
      .sort((a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0)).slice(0, limit) };
  }).filter(section => section.articles.length);
  // The existing rolling feed is chronological, independent of category placement.
  const listing = items.filter(item => !item.date || Date.parse(item.date) <= Date.now()).slice(0, 10);
  // Use recorded views only. Zero/missing counts must never become invented rankings.
  const mostRead = unique([...fromBlock("trending"), ...items])
    .filter(item => Number.isFinite(Number(item.raw.views)) && Number(item.raw.views) > 0)
    .sort((left, right) => Number(right.raw.views) - Number(left.raw.views) || (Date.parse(right.date) || 0) - (Date.parse(left.date) || 0))
    .slice(0, 6);
  return { categorySections, hero, listing, mostRead, all: items };
}

export function getMagazinePublication(settings = {}) {
  return {
    name: settings?.seo?.siteTitle || settings?.general?.publicationName || settings?.name || "Magazine",
    tagline: settings?.seo?.siteDescription || settings?.general?.tagline || "",
    logo: settings?.branding?.logo || settings?.logo || null,
    footerLogo: settings?.branding?.footerLogo || null,
  };
}
